import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { Skeleton } from 'antd';
import { observer } from 'mobx-react';

import { messageService } from '@classes/messageService';
import { SettingsManager } from '@classes/settingsManager';
import { PusherProvider } from '@common/contexts/Pusher';
import { useDevicePusher } from '@common/hooks/useDevicePusher';
import { FloatingReloadButton } from '@components/floatingReloadButton/floatingReloadButton';
import { NotFoundWithBack } from '@components/notFoundWithBack/notFoundWithBack';
import { TestEnvironmentBadge } from '@components/testEnvironmentBadge/testEnvironmentBadge';
import { ScheduleUseCases } from '@components/schedule/schedule';
import { TaskUseCases } from '@components/tasks/profile/taskProfile';
import { ApelsinFooter } from '@organisms/common/apelsinFooter';
import { ApelsinHeader } from '@organisms/common/apelsinHeader';
import StorageRoutes from '@organisms/storage/StorageRoutes';
import { TAB_ROUTES } from '@organisms/workers/common/constants';
import { TIncomingCallPayload } from '@pages/settingsCrm/contents/callSettings/modals/incomingCallModal';
import { IncomingCallWindow } from '@pages/settingsCrm/contents/callSettings/modals/incomingCallWindow';
import { rootStore } from '@store/rootStore/instanse';

import { useBackgroundStyle } from '../settingsUser/contents/BackgroundStyles/hooks/useBackgroundStyle';

import './app.scss';

const MailingRoutes = lazy(() => import('@pages/mailing/mailingRoutes'));
const ReviewRoutes = lazy(() => import('@organisms/review/reviewRoutes'));
const ContactsRoutes = lazy(() => import('@pages/contacts/contactsRoutes'));
const PromoRoutes = lazy(() => import('@pages/promo/promoRoutes'));
const CartRoutes = lazy(() => import('@pages/cart/cartRoutes'));
const CatalogRoutes = lazy(() => import('@pages/catalog/catalogRoutes'));

const ResourceRoutes = lazy(() => import('@pages/resource/ResourceRoutes'));
const EventList = lazy(() => import('@organisms/events/eventList'));
const TaskCategoryList = lazy(() => import('@components/tasks/category/taskCategoryList'));
const TaskCategoryProfile = lazy(() => import('@components/tasks/category/taskCategoryProfile'));
const TaskProfile = lazy(() => import('@components/tasks/profile/taskProfile'));
const TaskList = lazy(() => import('@components/tasks/taskList'));
const Administrator = lazy(() => import('@organisms/administrator/administrator'));
const Section = lazy(() => import('@organisms/administrator/partials/section/section'));
const CustomerList = lazy(() => import('@organisms/customers/customerList'));
const CustomerProfile = lazy(() => import('@organisms/customers/customerProfile'));
const InvitationList = lazy(() => import('@organisms/invitations/invitationList'));
const OrderList = lazy(() => import('@organisms/orders/orderList/orderList'));
const OrderProfile = lazy(() => import('@organisms/orders/orderProfile/orderProfile'));
const PermissionSettings = lazy(() => import('@organisms/permissionSettings/permissionSettings'));
const ProductList = lazy(() => import('@organisms/product/productList'));
const ProductProfile = lazy(() => import('@organisms/product/productProfile'));
const ProductCategoryList = lazy(() => import('@organisms/productCategory/productCategoryList'));
const ProductCategoryProfile = lazy(
    () => import('@organisms/productCategory/productCategoryProfile')
);
const СrmProfileRoutes = lazy(() => import('@pages/crmProfile/crmProfileRoutes'));

const ConnectionForm = lazy(() => import('@organisms/security/connectionForm'));
const LogList = lazy(() => import('@organisms/security/logList'));
const Logout = lazy(() => import('@organisms/security/logout'));
const TransactionList = lazy(() => import('@organisms/security/transactionList'));
const ServiceList = lazy(() => import('@organisms/service/serviceList'));
const ServiceProfile = lazy(() => import('@organisms/service/serviceProfile'));
const ServiceCategoryList = lazy(() => import('@organisms/serviceCategory/serviceCategoryList'));
const ServiceCategoryProfile = lazy(
    () => import('@organisms/serviceCategory/serviceCategoryProfile')
);

const SettingsCrmRoutes = lazy(() => import('@pages/settingsCrm/settingsRoutes'));
const SettingsUserRoutes = lazy(() => import('@pages/settingsUser/settingsRoutes'));

const NotificationsRoutes = lazy(() => import('@pages/notifications/notificationsRoutes'));
const OrderWorkflowRoutes = lazy(() => import('@pages/orderWorkflow/OrderWorkflowRoutes'));
const ObjectsRoutes = lazy(() => import('@pages/objects/ObjectsRoutes'));
const TokenVerification = lazy(() => import('@pages/tokenVerification/tokenVerification'));
const SpecialtyProfile = lazy(() => import('@organisms/specialty/specialtyProfile'));
const WorkerList = lazy(() => import('@organisms/workers/workerList'));
const WorkerProfile = lazy(() => import('@organisms/workers/workerProfile'));
const WorkplaceProfile = lazy(() => import('@organisms/workers/workplaceProfile/workplaceProfile'));
const UserProfile = lazy(() => import('@organisms/users/userProfile'));
const Schedule = lazy(() => import('@components/schedule/schedule'));

const AddressRoute = lazy(() => import('@organisms/address/addressRoute'));
const NotificationsLk = lazy(() => import('@organisms/notificationsLk/notificationsLk'));

const ChatRoutes = lazy(() => import('@pages/chat/chatRoutes'));

const Brigade = lazy(() => import('@organisms/brigade/brigade'));

const DevicePusherConnector: React.FC<{ crmId?: number }> = ({ crmId }) => {
    const [isIncomingCallModalOpen, setIsIncomingCallModalOpen] = useState(false);
    const [incomingCallPayload, setIncomingCallPayload] = useState<TIncomingCallPayload | null>(
        null
    );

    const openCallKeyRef = useRef<string | null>(null);

    // The device channel also gets `ring` events (for phones that receive the call).
    // `idle` only updates the modal of the same call, it never opens a new one.
    const handleIncomingCall = useCallback((payload: TIncomingCallPayload & { type?: string; call_id?: string }) => {
        if (payload?.type !== 'modal') return;

        const callKey = String(payload.call_id ?? payload.phone);
        if (payload.state === 'idle' && openCallKeyRef.current !== callKey) return;

        openCallKeyRef.current = callKey;
        setIncomingCallPayload(payload);
        setIsIncomingCallModalOpen(true);
    }, []);

    const handleCloseIncomingCall = useCallback(() => {
        openCallKeyRef.current = null;
        setIsIncomingCallModalOpen(false);
    }, []);

    useDevicePusher({ crmId, onIncomingCall: handleIncomingCall });

    return (
        <IncomingCallWindow
            open={isIncomingCallModalOpen}
            onClose={handleCloseIncomingCall}
            payload={incomingCallPayload}
        />
    );
};

const App = observer((): JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();

    // Кука сессии читается на каждый рендер. useMemo([]) запоминал пустой токен
    // со страницы входа, и после логина без перезагрузки сокет так и не поднимался.
    const creds = SettingsManager.getConnectionCredentials();
    // Подписка на стор: setUser после логина перерисовывает App со свежим токеном.
    const userId = rootStore.currentUserStore.getUser?.id || creds?.userId;

    const { backgroundStyle } = useBackgroundStyle();

    function checkConnection() {
        const connection = SettingsManager.getConnectionCredentials();
        if (connection?.token != null) {
            if (connection.crmID && location?.pathname == '/') {
                navigate(`/lk/worker/crm/${connection.crmID}`);
            } else if (location?.pathname == '/') navigate('/lk');
        } else {
            if (location?.pathname != '/login' && location?.pathname != '/token-verification')
                navigate('/login');
        }
    }

    useEffect(() => {
        checkConnection();
        rootStore.navigationStore.pushLastPath(location.pathname);
        messageService.clearMessages();
    }, [location.pathname]);

    function getBody(): JSX.Element {
        return (
            <Suspense fallback={<Skeleton active />}>
                <Routes>
                    <Route
                        path="*"
                        element={
                            <NotFoundWithBack
                                backPath="/lk"
                                backLabel="Вернуться в личный кабинет"
                            />
                        }
                    />
                    <Route path="/login" element={<ConnectionForm />} />
                    <Route path="/token-verification" element={<TokenVerification />} />
                    <Route path="/lk" element={<UserProfile />} />
                    <Route path="/address" element={<AddressRoute />} />

                    <Route path="/logout" element={<Logout />} />
                    <Route path="/lk/worker/crm/*" element={<СrmProfileRoutes />} />

                    <Route
                        path="/lk/worker/crm/:crmID/schedule"
                        element={<Schedule useCase={ScheduleUseCases.CRM_View} />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/schedule/edit"
                        element={<Schedule useCase={ScheduleUseCases.CRM_Edit} />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/schedule/templates/edit"
                        element={<Schedule useCase={ScheduleUseCases.Template_Edit} />}
                    />
                    <Route path="/lk/worker/crm/:id/invitations" element={<InvitationList />} />
                    <Route
                        path="/lk/worker/crm/:id/workers/tabs/:currentTab"
                        element={<WorkerList />}
                    />
                    <Route
                        path={`/lk/worker/crm/:id/workers/tabs/${TAB_ROUTES.GROUPS}/brigade/create`}
                        element={<Brigade isEdit />}
                    />
                    <Route
                        path={`/lk/worker/crm/:id/workers/tabs/${TAB_ROUTES.GROUPS}/brigade/:brigadeId`}
                        element={<Brigade />}
                    />
                    <Route
                        path={`/lk/worker/crm/:id/workers/tabs/${TAB_ROUTES.GROUPS}/brigade/:brigadeId/edit`}
                        element={<Brigade isEdit />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/workers/tabs/:currentTab/:groupId/workplaces"
                        element={<WorkerList />}
                    />
                    {/* Создание рабочего места */}
                    <Route
                        path="/lk/worker/crm/:id/workers/tabs/:currentTab/:groupId/workplaces/create"
                        element={<WorkplaceProfile />}
                    />
                    {/* Редактирование рабочего места */}
                    <Route
                        path="/lk/worker/crm/:id/workers/tabs/:currentTab/:groupId/workplaces/:workplaceId"
                        element={<WorkplaceProfile />}
                    />
                    <Route path="/lk/worker/profile" element={<WorkerProfile />} />
                    <Route path="/lk/worker/profile/edit" element={<WorkerProfile />} />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionId/user/:userId"
                        element={<WorkerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionId"
                        element={<WorkerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionId/create"
                        element={<WorkerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionId/user/:userId/edit"
                        element={<WorkerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/customer-profile/:customerProfileId/user/:userId/edit"
                        element={<WorkerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/customer-profile/:customerProfileId/user/:userId"
                        element={<WorkerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionId/edit"
                        element={<WorkerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/users/:workerPositionId"
                        element={<UserProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionID/schedule"
                        element={<Schedule useCase={ScheduleUseCases.WorkerPosition_View} />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionID/schedule/edit"
                        element={<Schedule useCase={ScheduleUseCases.WorkerPosition_Edit} />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionId/permissions"
                        element={<PermissionSettings />}
                    />
                    <Route path="/lk/worker/crm/:crmID/customers" element={<CustomerList />} />
                    <Route
                        path="/lk/worker/crm/:crmID/customers/:customerID"
                        element={<CustomerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/customers/:phone/create"
                        element={<CustomerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmID/customers/:customerID/edit"
                        element={<CustomerProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/specialties/:specialtyID"
                        element={<SpecialtyProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/specialties/:specialtyID/edit"
                        element={<SpecialtyProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/product-category"
                        element={<ProductCategoryList />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/product-category/:productCategoryID"
                        element={<ProductCategoryProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/product-category/create"
                        element={<ProductCategoryProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/product-category/:productCategoryID/edit"
                        element={<ProductCategoryProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmId/storages/:storageID/products"
                        element={<ProductList />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmId/storages/:storageID/products/nav/:productCategoryId"
                        element={<ProductList />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmId/storages/:storageID/products/:productId"
                        element={<ProductProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmId/storages/:storageID/products/create"
                        element={<ProductProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmId/storages/:storageID/products/:productId/create"
                        element={<ProductProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:crmId/storages/:storageID/products/:productId/edit"
                        element={<ProductProfile />}
                    />
                    {/*
                        Редактирование товара без привязки к складу. Нужно для перехода
                        из заказа: строка таблицы приходит из Order/ProductResource,
                        а он склад не отдаёт. Возврат назад идёт через ?returnTo=…
                    */}
                    <Route
                        path="/lk/worker/crm/:crmId/products/:productId/edit"
                        element={<ProductProfile />}
                    />
                    <Route path="/lk/worker/crm/:crmId/storages/*" element={<StorageRoutes />} />

                    <Route
                        path="/lk/worker/crm/:id/service-categories"
                        element={<ServiceCategoryList />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/service-categories/:serviceCategoryID"
                        element={<ServiceCategoryProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/service-categories/create"
                        element={<ServiceCategoryProfile />}
                    />

                    <Route
                        path="/lk/worker/crm/:id/service-categories/:serviceCategoryID/edit"
                        element={<ServiceCategoryProfile />}
                    />
                    <Route path="/lk/worker/crm/:id/services" element={<ServiceList />} />
                    <Route
                        path="/lk/worker/crm/:crmID/resource_groups/*"
                        element={<ResourceRoutes />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/services/nav/:serviceCategoryId"
                        element={<ServiceList />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/services/:serviceID"
                        element={<ServiceProfile />}
                    />
                    <Route path="/lk/worker/crm/:id/services/create" element={<ServiceProfile />} />
                    <Route
                        path="/lk/worker/crm/:id/services/:serviceID/edit"
                        element={<ServiceProfile />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/task-categories"
                        element={<TaskCategoryList />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/task-categories/:taskCategoryID"
                        element={<TaskCategoryProfile useCase={TaskUseCases.Profile} />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/task-categories/create"
                        element={<TaskCategoryProfile useCase={TaskUseCases.Create} />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/task-categories/:taskCategoryID/edit"
                        element={<TaskCategoryProfile useCase={TaskUseCases.Edit} />}
                    />
                    <Route path="/lk/worker/crm/:id/tasks" element={<TaskList />} />
                    <Route
                        path="/lk/worker/crm/:id/tasks/nav/:taskCategoryId"
                        element={<TaskList />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/tasks/create"
                        element={<TaskProfile useCase={TaskUseCases.Create} />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/tasks/:taskID"
                        element={<TaskProfile useCase={TaskUseCases.Profile} />}
                    />
                    <Route
                        path="/lk/worker/crm/:id/tasks/:taskID/edit"
                        element={<TaskProfile useCase={TaskUseCases.Edit} />}
                    />
                    <Route path="/lk/worker/crm/:id/orders" element={<OrderList />} />
                    <Route path="/lk/worker/crm/:id/orders/create" element={<OrderList />} />
                    <Route path="/lk/worker/crm/:id/orders/:orderID" element={<OrderProfile />} />
                    <Route
                        path="/lk/worker/crm/:id/orders/:orderID/workflow"
                        element={<OrderWorkflowRoutes />}
                    />
                    {/** Временное решение для демонстрации изменений URI */}
                    <Route
                        path="/lk/worker/crm/:id/orders/:orderID/:worksheetName"
                        element={<OrderProfile />}
                    />
                    {/** */}
                    {/*<Route path="/lk/worker/crm/:id/orders/:orderID/edit" element={<OrderEdit />} />*/}
                    <Route path="/lk/worker/crm/:id/logs" element={<LogList />} />
                    <Route path="/lk/worker/crm/:id/transactions" element={<TransactionList />} />

                    <Route path="/lk/settings/*" element={<SettingsUserRoutes />} />
                    <Route path="/lk/worker/crm/:id/settings/*" element={<SettingsCrmRoutes />} />

                    <Route path="/lk/events" element={<EventList />} />
                    <Route path="lk/notifications" element={<NotificationsLk />} />
                    <Route path="/lk/catalog/*" element={<CatalogRoutes />} />
                    <Route path="/lk/cart/*" element={<CartRoutes />} />
                    <Route path="/lk/chat-groups/*" element={<ChatRoutes />} />
                    <Route path="/lk/admin" element={<Administrator />} />
                    <Route path="/lk/admin/:sectionCode/*" element={<Section />} />
                    <Route
                        path="/lk/worker/crm/:id/notifications/*"
                        element={<NotificationsRoutes />}
                    />
                    <Route path="/lk/objects/*" element={<ObjectsRoutes />} />
                    <Route
                        path="/lk/worker/crm/:id/customers/:customerId/objects/*"
                        element={<ObjectsRoutes />}
                    />
                    <Route path="/lk/worker/crm/:crmID/promo/*" element={<PromoRoutes />} />
                    <Route path="/lk/worker/crm/:crmID/mailing/*" element={<MailingRoutes />} />
                    <Route
                        path="/lk/worker/crm/:crmID/workers/:workerPositionId/reviews/*"
                        element={<ReviewRoutes />}
                    />
                    <Route path="/lk/contacts/*" element={<ContactsRoutes />} />
                </Routes>
            </Suspense>
        );
    }

    return (
        <div className="app-global">
            <PusherProvider userId={userId} token={creds?.token}>
                <TestEnvironmentBadge />
                <FloatingReloadButton />
                <ApelsinHeader />
                {creds?.token ? <DevicePusherConnector crmId={creds.crmID || 1} /> : null}
                <div id="apelsin-app-content" style={backgroundStyle}>
                    {getBody()}
                </div>
                <ApelsinFooter />
            </PusherProvider>
        </div>
    );
});

export { App };
